import { Component, ElementRef } from '@angular/core';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxMaskDirective } from 'ngx-mask';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDatepickerModule, BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
// Third Party
import { BsModalService, BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import {
  Events,
  FirearmTypes,
  ModalID,
  Statements,
} from 'src/app/shared/models/constants';
import {
  BoundBookEntry,
  FromValues,
  ToValues,
} from 'src/app/shared/models/general';
import {
  createDateTimeStamp,
  createEventRecord,
  findFfl,
} from '../../functions';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';

@Component({
  selector: 'app-edit-bb-acquisition',
  templateUrl: './edit-bb-acquisition.component.html',
  styleUrls: ['./edit-bb-acquisition.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, NgxMaskDirective, TooltipModule, BsDatepickerModule],
})
export class EditBbAcquisitionComponent {
  edit_acquisition_form: FormGroup;
  firearm_types = FirearmTypes;
  bound_book_entry: BoundBookEntry;
  serial_editing_blackout: string = 'rgba(255, 255, 255, 0.05)';
  selected_city_options: string[] = [];
  today: Date = new Date();

  duplicate_enabled_entry = true;
  duplicate_entry = true;

  // Modals
  modalRef: BsModalRef;
  modalConfig = { animated: true };
  // Datepicker
  bsConfig: Partial<BsDatepickerConfig>;

  constructor(
    protected element: ElementRef,
    private modalService: BsModalService,
    private fb: FormBuilder,
    private sharedService: SharedService,
  ) {
    // Initializing Form
    this.edit_acquisition_form = this.fb.group({
      ffl: new FormControl({ value: null, disabled: false }),
      received_from_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      business_name: new FormControl({ value: null, disabled: false }),
      address: new FormControl({ value: null, disabled: false }),
      address2: new FormControl({ value: null, disabled: false }),
      zip_code: new FormControl({ value: null, disabled: false }),
      state: new FormControl({ value: null, disabled: false }),
      city: new FormControl({ value: null, disabled: false }),
      date_acquired: new FormControl({ value: this.today, disabled: false }, [
        Validators.required,
      ]),
      edit_note: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
    });
  }
  ngOnInit(): void {
    this.edit_acquisition_form.patchValue({
      ffl: this.bound_book_entry.a_ffl,
      received_from_name: this.bound_book_entry.a_received_from_name,
      business_name: this.bound_book_entry.a_business_name,
      address: this.bound_book_entry.a_address,
      address2: this.bound_book_entry.a_address2,
      zip_code: this.bound_book_entry.a_zip_code,
      state: this.bound_book_entry.a_state,
      city: this.bound_book_entry.a_city,
      date_acquired: this.bound_book_entry.a_date_acquired,
    });
  }
  closeModalClick(): void {
    this.edit_acquisition_form.reset();
    // this.bound_book_entry = new Firearm();
    this.modalService.hide(ModalID.edit_bb_acquisition);
  }
  editAcquisitionClick() {
    if (this.checkForValuesChanges()) {
      const original_acquisition_values = JSON.parse(
        JSON.stringify(this.bound_book_entry),
      );
      const value_differences = this.returnValueChanges();

      // Convert changes array to from_values and to_values objects
      const from_values: any = {};
      const to_values: any = {};
      value_differences.forEach((change) => {
        from_values[change.key] = change.from;
        to_values[change.key] = change.to;
      });

      this.bound_book_entry.a_ffl = this.edit_acquisition_form.value.ffl;
      this.bound_book_entry.a_received_from_name =
        this.edit_acquisition_form.value.received_from_name;
      this.bound_book_entry.a_business_name =
        this.edit_acquisition_form.value.business_name;
      this.bound_book_entry.a_address =
        this.edit_acquisition_form.value.address;
      this.bound_book_entry.a_address2 =
        this.edit_acquisition_form.value.address2;
      this.bound_book_entry.a_zip_code =
        this.edit_acquisition_form.value.zip_code;
      this.bound_book_entry.a_state = this.edit_acquisition_form.value.state;
      this.bound_book_entry.a_city = this.edit_acquisition_form.value.city;
      this.bound_book_entry.a_date_acquired =
        this.edit_acquisition_form.value.date_acquired;
      this.bound_book_entry.notes.push({
        note_type: Events.modify_acquisition,
        comment: this.edit_acquisition_form.value.edit_note,
        username: this.sharedService.logged_in_user.username,
        dateTime: createDateTimeStamp(),
      });
      this.bound_book_entry.event_log.push(
        createEventRecord(
          Events.modify_acquisition,
          from_values,
          to_values,
          this.sharedService.logged_in_user.username,
        ),
      );
      this.sharedService.firearm_to_edit$.next(this.bound_book_entry);
      this.closeModalClick();
    } else {
      this.sharedService.openMessageModal(Statements.no_values_changed);
    }
  }
  checkForValuesChanges() {
    if (
      this.edit_acquisition_form.value.ffl !== this.bound_book_entry.a_ffl ||
      this.edit_acquisition_form.value.received_from_name !==
        this.bound_book_entry.a_firearm_type ||
      this.edit_acquisition_form.value.business_name !==
        this.bound_book_entry.a_serial_number ||
      this.edit_acquisition_form.value.address !==
        this.bound_book_entry.a_importer ||
      this.edit_acquisition_form.value.address2 !==
        this.bound_book_entry.a_model ||
      this.edit_acquisition_form.value.zip_code !==
        this.bound_book_entry.a_additional_markings ||
      this.edit_acquisition_form.value.state !==
        this.bound_book_entry.a_caliber_gauge ||
      this.edit_acquisition_form.value.city !== this.bound_book_entry.a_city ||
      this.edit_acquisition_form.value.date_acquired !==
        this.bound_book_entry.a_date_acquired
    ) {
      return true;
    } else {
      return false;
    }
  }
  returnValueChanges(): Array<{ key: string; from: any; to: any }> {
    const changes: Array<{ key: string; from: any; to: any }> = [];

    if (this.edit_acquisition_form.value.ffl !== this.bound_book_entry.a_ffl) {
      changes.push({
        key: 'ffl',
        from: this.bound_book_entry.a_ffl || '',
        to: this.edit_acquisition_form.value.ffl,
      });
    }
    if (
      this.edit_acquisition_form.value.received_from_name !==
      this.bound_book_entry.a_received_from_name
    ) {
      changes.push({
        key: 'received_from_name',
        from: this.bound_book_entry.a_received_from_name || '',
        to: this.edit_acquisition_form.value.received_from_name,
      });
    }
    if (
      this.edit_acquisition_form.value.business_name !==
      this.bound_book_entry.a_business_name
    ) {
      changes.push({
        key: 'business_name',
        from: this.bound_book_entry.a_business_name || '',
        to: this.edit_acquisition_form.value.business_name,
      });
    }
    if (
      this.edit_acquisition_form.value.address !==
      this.bound_book_entry.a_address
    ) {
      changes.push({
        key: 'address',
        from: this.bound_book_entry.a_address || '',
        to: this.edit_acquisition_form.value.address,
      });
    }
    if (
      this.edit_acquisition_form.value.address2 !==
      this.bound_book_entry.a_address2
    ) {
      changes.push({
        key: 'address2',
        from: this.bound_book_entry.a_address2 || '',
        to: this.edit_acquisition_form.value.address2,
      });
    }
    if (
      this.edit_acquisition_form.value.zip_code !==
      this.bound_book_entry.a_zip_code
    ) {
      changes.push({
        key: 'zip_code',
        from: this.bound_book_entry.a_zip_code || '',
        to: this.edit_acquisition_form.value.zip_code,
      });
    }
    if (
      this.edit_acquisition_form.value.state !== this.bound_book_entry.a_state
    ) {
      changes.push({
        key: 'state',
        from: this.bound_book_entry.a_state || '',
        to: this.edit_acquisition_form.value.state,
      });
    }
    if (
      this.edit_acquisition_form.value.city !== this.bound_book_entry.a_city
    ) {
      changes.push({
        key: 'city',
        from: this.bound_book_entry.a_city || '',
        to: this.edit_acquisition_form.value.city,
      });
    }
    if (
      this.edit_acquisition_form.value.date_acquired !==
      this.bound_book_entry.a_date_acquired
    ) {
      changes.push({
        key: 'date_acquired',
        from: this.bound_book_entry.a_date_acquired || '',
        to: this.edit_acquisition_form.value.date_acquired,
      });
    }

    return changes;
  }
  fetchFfl() {
    findFfl(
      this.sharedService,
      this.edit_acquisition_form.get('ffl').value.toUpperCase(),
    );
    // let fflFound: FFL;
    // for (const ffl of this.sharedService.ffl_list) {
    //   if (ffl.ffl === this.acquisition_form.get('ffl').value.toUpperCase()) {
    //     fflFound = ffl;
    //     this.sharedService.openYesNoModal(
    //       Questions.correct_ffl,
    //       fflFound,
    //       true
    //     );
    //   }
    // }
  }
  findStateAndCity() {
    this.selected_city_options = [];
    // for (const city of ZipCodes) {
    //   if (city.zip_code === this.edit_acquisition_form.get('zip_code').value) {
    //     this.selected_city_options.push(city);
    //   }
    // }
  }
}
